import { createLogger } from "@/logger";

const log = createLogger("bootstrap");

import { workspaceId as activeWorkspaceId, localDBVersion } from "@/db/db";
import { retryWithBackoff } from "@/db/storageRetry";
import { isQuotaExceededError } from "@/utils/localStorage";

type BootstrapEventMetadata = {
  name: string;
  tableName?: string;
  initialBootstrapTime?: string; // ISO string
  latestBootstrapTime?: string; // ISO string (last insertBootstrapEvent call)
  mostRecentUpdateTime?: string; // ISO string (last touch/update)
};

type WorkspaceRegistryRecord = {
  workspaceId: string;
  events: Record<string, BootstrapEventMetadata>;
  version: number;
};

export const IDB_DB_NAME = `${localDBVersion}-sbweb-bootstrap-registry`;
const IDB_STORE_NAME = "workspaces";
const IDB_VERSION = 1;
const OPEN_TIMEOUT_MS = 10_000;

async function openRegistryDb(): Promise<IDBDatabase> {
  return await retryWithBackoff(
    () =>
      new Promise<IDBDatabase>((resolve, reject) => {
        let settled = false;
        const settle = (action: () => void): void => {
          if (settled) return;
          settled = true;
          action();
        };

        const request = window.indexedDB.open(IDB_DB_NAME, IDB_VERSION);
        const timer = setTimeout(() => settle(() => reject(new Error("IndexedDB open timeout"))), OPEN_TIMEOUT_MS);

        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
            db.createObjectStore(IDB_STORE_NAME, { keyPath: "workspaceId" });
          }
        };

        request.onsuccess = () => {
          clearTimeout(timer);
          if (settled) {
            request.result.close();
            return;
          }
          settle(() => resolve(request.result));
        };
        request.onerror = () => {
          clearTimeout(timer);
          const error = request.error ?? new Error("Unknown IndexedDB error");
          log.error("[BOOTSTRAP][REGISTRY] Failed to open IndexedDB", { error });
          settle(() => reject(error));
        };
        request.onblocked = () => {
          clearTimeout(timer);
          log.warn("[BOOTSTRAP][REGISTRY] IndexedDB blocked - close other tabs?");
          settle(() => reject(new Error("IndexedDB blocked by another connection")));
        };
      }),
    {
      maxRetries: 3,
      initialDelayMs: 500,
      label: "openRegistryDb",
      // QuotaExceededError won't recover by retrying - fail fast.
      shouldRetry: (e) => !isQuotaExceededError(e),
    },
  );
}

async function readWorkspaceRecord(db: IDBDatabase, workspaceId: string): Promise<WorkspaceRegistryRecord> {
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE_NAME, "readonly");
    const store = tx.objectStore(IDB_STORE_NAME);
    const getReq = store.get(workspaceId);
    getReq.onsuccess = () => {
      const existing: WorkspaceRegistryRecord | undefined = getReq.result as WorkspaceRegistryRecord | undefined;
      if (existing) {
        resolve(existing);
      } else {
        resolve({ workspaceId, events: {}, version: IDB_VERSION });
      }
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

async function writeWorkspaceRecord(db: IDBDatabase, record: WorkspaceRegistryRecord): Promise<void> {
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE_NAME, "readwrite");
    const store = tx.objectStore(IDB_STORE_NAME);
    const putReq = store.put(record);
    putReq.onsuccess = () => resolve();
    putReq.onerror = () => {
      if (isQuotaExceededError(putReq.error)) {
        log.warn("[BOOTSTRAP][REGISTRY] QuotaExceededError writing workspace record, skipping persist");
        resolve();
        return;
      }
      reject(putReq.error);
    };
    tx.onabort = () => {
      if (isQuotaExceededError(tx.error)) {
        log.warn("[BOOTSTRAP][REGISTRY] QuotaExceededError on transaction abort, skipping persist");
        resolve();
        return;
      }
      reject(tx.error ?? new DOMException("Transaction aborted", "AbortError"));
    };
  });
}

export class BootstrapEventsRegistry {
  private static instance: BootstrapEventsRegistry | null = null;

  static getInstance(): BootstrapEventsRegistry {
    if (!BootstrapEventsRegistry.instance) {
      BootstrapEventsRegistry.instance = new BootstrapEventsRegistry();
    }
    return BootstrapEventsRegistry.instance;
  }

  private constructor() {}

  public async getLatestEventDate(name: string, workspaceId?: string): Promise<Date | undefined> {
    return await this.withDb(async (db: IDBDatabase): Promise<Date | undefined> => {
      try {
        const wsId = this.normalizeWorkspaceId(workspaceId);
        const record = await readWorkspaceRecord(db, wsId);
        const metadata = record.events[name];
        const source = metadata?.mostRecentUpdateTime
          ? "mostRecentUpdateTime"
          : metadata?.latestBootstrapTime
            ? "latestBootstrapTime"
            : "none";
        const iso = metadata?.mostRecentUpdateTime ?? metadata?.latestBootstrapTime;
        log.info("[BOOTSTRAP][REGISTRY] getLatestEventDate", {
          name,
          workspaceId: wsId,
          source,
          iso,
        });
        return iso ? new Date(iso) : undefined;
      } catch (e) {
        log.warn("[BOOTSTRAP][REGISTRY] getLatestEventDate failed", {
          name,
          workspaceId: this.normalizeWorkspaceId(workspaceId),
          error: e,
        });
        return undefined;
      }
    });
  }

  public async touchUpdate(name: string, workspaceId?: string): Promise<void> {
    await this.withDb(async (db: IDBDatabase): Promise<void> => {
      const record = await readWorkspaceRecord(db, this.normalizeWorkspaceId(workspaceId));

      const existing = record.events[name] ?? { name };
      existing.mostRecentUpdateTime = new Date().toISOString();
      record.events[name] = existing as BootstrapEventMetadata;

      await writeWorkspaceRecord(db, record);
    });
  }

  public async markBootstrap(
    name: string,
    tableName?: string,
    workspaceId?: string,
    recordedAt?: Date,
  ): Promise<BootstrapEventMetadata> {
    return await this.withDb(async (db: IDBDatabase): Promise<BootstrapEventMetadata> => {
      const record = await readWorkspaceRecord(db, this.normalizeWorkspaceId(workspaceId));

      const nowIso = (recordedAt ?? new Date()).toISOString();
      const existing = record.events[name];
      const metadata: BootstrapEventMetadata = {
        name,
        tableName,
        initialBootstrapTime: existing?.initialBootstrapTime ?? nowIso,
        latestBootstrapTime: nowIso,
        mostRecentUpdateTime: nowIso,
      };
      record.events[name] = metadata;

      await writeWorkspaceRecord(db, record);

      return metadata;
    });
  }

  public async hasBootstrapForWorkspace(workspaceId?: string): Promise<boolean> {
    try {
      return await this.withDb(async (db: IDBDatabase): Promise<boolean> => {
        const record = await readWorkspaceRecord(db, this.normalizeWorkspaceId(workspaceId));
        return Boolean(record.events["bootstrap"]);
      });
    } catch (_e) {
      return false;
    }
  }

  public async clearWorkspace(workspaceId?: string): Promise<void> {
    await this.withDb(async (db: IDBDatabase): Promise<void> => {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(IDB_STORE_NAME, "readwrite");
        const store = tx.objectStore(IDB_STORE_NAME);
        const del = store.delete(this.normalizeWorkspaceId(workspaceId));
        del.onsuccess = () => resolve();
        del.onerror = () => reject(del.error);
      });
    });
  }

  public async clearAll(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const req = window.indexedDB.deleteDatabase(IDB_DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private normalizeWorkspaceId(explicitWorkspaceId?: string): string {
    return explicitWorkspaceId ?? activeWorkspaceId ?? "unknown";
  }

  private async withDb<T>(callback: (db: IDBDatabase) => Promise<T>): Promise<T> {
    const db = await openRegistryDb();
    const result = await callback(db);
    db.close();
    return result;
  }
}
